import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import { api, apiPost, apiPut, apiDelete } from '../lib/api'
import { RoadmapGallery } from '../components/RoadmapGallery'
import { ConfirmDialog, PromptDialog } from '../components/ConfirmDialog'
import { useTabParam } from '../hooks/useTabParam'
import '../styles/roadmap.css'

// ── Types ──────────────────────────────────────────────────────────────
interface Category { id: string; name: string; sortOrder: number; isActive: boolean }
interface Product {
    id: string; name: string; description?: string; color?: string; icon?: string
    sortOrder: number; isActive: boolean; categories: Category[]
}
interface Phase {
    id: string; name: string; description?: string; color?: string; icon?: string
    sortOrder: number; isDefault: boolean; isTerminal: boolean; isActive: boolean; requestCount: number
}
interface AdminRequest {
    id: string; title: string; description: string; problem?: string; plannedSolution?: string
    productId?: string; categoryId?: string; phaseId?: string; status: string
    declineReason?: string; targetRelease?: string; imageUrl?: string; isHeadOfficeOnly?: boolean
    images?: { url: string; width?: number; height?: number }[]
    voteCount: number; voteScore: number; submitter?: string; submitterEmail?: string; submittedByUserId?: string
    commentCount: number; externalProvider?: string; externalUrl?: string
    createdAt: string; updatedAt?: string; publishedAt?: string
}

type OwnerUser = { id: string; displayName: string; email: string; avatarUrl?: string | null }

const fmtDate = (s?: string) =>
    s ? new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''

// Sent for unset product/category/phase selects — the API maps Guid.Empty to "clear".
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000'

type Tab = 'new' | 'requests' | 'phases' | 'products'
const TABS: readonly Tab[] = ['new', 'requests', 'phases', 'products']

// ════════════════════════════════════════════════════════════════════════
export default function AdminRoadmapPage() {
    const token = useAuthStore(s => s.accessToken)
    const [tab, setTab] = useTabParam<Tab>(TABS, 'new')

    const { data: reqData } = useQuery<{ requests: AdminRequest[] }>({
        queryKey: ['admin-roadmap-requests'],
        queryFn: () => api('/api/admin/roadmap/requests', token),
    })
    const { data: prodData } = useQuery<{ products: Product[] }>({
        queryKey: ['admin-roadmap-products'],
        queryFn: () => api('/api/admin/roadmap/products', token),
    })
    const { data: phaseData } = useQuery<{ phases: Phase[] }>({
        queryKey: ['admin-roadmap-phases'],
        queryFn: () => api('/api/admin/roadmap/phases', token),
    })

    const requests = reqData?.requests ?? []
    const products = prodData?.products ?? []
    const phases = phaseData?.phases ?? []
    const pendingCount = requests.filter(r => r.status === 'Pending' || r.status === 'PendingConfirmation').length

    return (
        <>
            <div className="page-header">
                <h1>🗺️ Roadmap Management</h1>
                <p style={{ color: 'var(--color-text-secondary)', marginTop: 4 }}>
                    Review new submissions, move requests through the pipeline, and manage products, categories, and phases.
                </p>
            </div>

            <div className="admin-subtabs">
                <button className={`admin-subtab${tab === 'new' ? ' active' : ''}`} onClick={() => setTab('new')}>
                    New{pendingCount > 0 && <span className="tab-count">{pendingCount}</span>}
                </button>
                <button className={`admin-subtab${tab === 'requests' ? ' active' : ''}`} onClick={() => setTab('requests')}>All Requests</button>
                <button className={`admin-subtab${tab === 'phases' ? ' active' : ''}`} onClick={() => setTab('phases')}>Phases</button>
                <button className={`admin-subtab${tab === 'products' ? ' active' : ''}`} onClick={() => setTab('products')}>Products &amp; Categories</button>
            </div>

            {tab === 'new' && <NewSubmissionsTab requests={requests.filter(r => r.status === 'Pending' || r.status === 'PendingConfirmation')} products={products} phases={phases} />}
            {tab === 'requests' && <RequestsTab requests={requests} products={products} phases={phases} />}
            {tab === 'phases' && <PhasesTab phases={phases} />}
            {tab === 'products' && <ProductsTab products={products} />}
        </>
    )
}

// ════════════════════════════════════════════════════════════════════════
//  New submissions
// ════════════════════════════════════════════════════════════════════════
function NewSubmissionsTab({ requests, products, phases }: { requests: AdminRequest[]; products: Product[]; phases: Phase[] }) {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const [editing, setEditing] = useState<AdminRequest | null>(null)
    const [removing, setRemoving] = useState<AdminRequest | null>(null)
    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['admin-roadmap-requests'] })
        qc.invalidateQueries({ queryKey: ['admin-roadmap-pending-count'] }) // refresh sidebar badge
    }

    const publishMut = useMutation({
        mutationFn: (p: { id: string; body: any }) => apiPost(`/api/admin/roadmap/requests/${p.id}/publish`, token, p.body),
        onSuccess: invalidate,
    })
    const declineMut = useMutation({
        mutationFn: (p: { id: string; reason: string }) => apiPost(`/api/admin/roadmap/requests/${p.id}/decline`, token, { reason: p.reason }),
        onSuccess: invalidate,
    })
    // Full editing (product/category/phase/status/problem/solution) happens in the shared
    // Edit modal so the submission card itself stays concise. Archiving and the inline "HO only"
    // toggle reuse the same PUT. onSettled (not just onSuccess) so a failed toggle refetches and
    // the optimistic switch snaps back to the real value.
    const updateMut = useMutation({
        mutationFn: (p: { id: string; body: any }) => apiPut(`/api/admin/roadmap/requests/${p.id}`, token, p.body),
        onSettled: invalidate,
    })

    if (!requests.length) {
        return <div className="empty-state"><div className="icon">✅</div><h3>You're all caught up</h3><p>New submissions will appear here for review.</p></div>
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {requests.map(r => (
                <NewSubmissionCard
                    key={r.id}
                    req={r}
                    products={products}
                    phases={phases}
                    onEdit={() => setEditing(r)}
                    onPublish={(body) => publishMut.mutate({ id: r.id, body })}
                    onRemove={() => setRemoving(r)}
                    onSetHeadOffice={(v) => updateMut.mutate({ id: r.id, body: { isHeadOfficeOnly: v } })}
                />
            ))}

            {editing && (
                <EditRequestModal
                    req={editing}
                    products={products}
                    phases={phases}
                    onClose={() => setEditing(null)}
                    onSave={(body) => { updateMut.mutate({ id: editing.id, body }); setEditing(null) }}
                />
            )}

            {removing && (
                <DeleteRequestModal
                    req={removing}
                    onArchive={() => { updateMut.mutate({ id: removing.id, body: { status: 'Archived' } }); setRemoving(null) }}
                    onReject={(reason) => { declineMut.mutate({ id: removing.id, reason }); setRemoving(null) }}
                    onClose={() => setRemoving(null)}
                />
            )}
        </div>
    )
}

function NewSubmissionCard({ req, products, phases, onEdit, onPublish, onRemove, onSetHeadOffice }: {
    req: AdminRequest; products: Product[]; phases: Phase[]
    onEdit: () => void; onPublish: (body: any) => void; onRemove: () => void
    onSetHeadOffice: (value: boolean) => void
}) {
    // Kept as local state for a snappy toggle, but re-synced whenever the request reloads (e.g.
    // after it's changed in the Edit popup) so the switch never shows a stale value.
    const [headOfficeOnly, setHeadOfficeOnly] = useState(req.isHeadOfficeOnly ?? false)
    useEffect(() => { setHeadOfficeOnly(req.isHeadOfficeOnly ?? false) }, [req.isHeadOfficeOnly])

    const product = products.find(p => p.id === req.productId)
    const category = product?.categories.find(c => c.id === req.categoryId)
    // The phase the card will publish into: the request's own, else the default.
    const phase = (req.phaseId ? phases.find(p => p.id === req.phaseId) : undefined) ?? phases.find(p => p.isDefault)
    const accent = product?.color ?? 'var(--color-border)'
    const tint = (c?: string) => (c ?? '#888') + '22'
    const initials = (req.submitter ?? req.submitterEmail ?? 'A')
        .trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()

    return (
        <div className="submission-card">
            <div className="submission-rail" style={{ background: accent }} />
            <div className="submission-body">
                <div className="submission-head">
                    <div className="submission-title">{req.title}</div>
                    {req.status === 'PendingConfirmation' && (
                        <span className="pending-confirmation-badge">📬 Awaiting confirmation</span>
                    )}
                    <button className="submission-del" title="Remove request…"
                        onClick={onRemove}>🗑️</button>
                </div>

                {req.description && <div className="submission-desc">{req.description}</div>}

                <div className="submission-chips">
                    {product && (
                        <span className="submission-chip" style={{ background: tint(product.color), color: product.color ?? undefined, borderColor: (product.color ?? '#888') + '55' }}>
                            {product.icon && <span>{product.icon}</span>}{product.name}
                        </span>
                    )}
                    {category && <span className="submission-chip">{category.name}</span>}
                    {phase && (
                        <span className="submission-chip" style={{ background: tint(phase.color), color: phase.color ?? undefined, borderColor: (phase.color ?? '#888') + '55' }}>
                            {phase.icon && <span>{phase.icon}</span>}{phase.name}
                        </span>
                    )}
                </div>

                <div className="submission-foot">
                    <div className="submission-avatar" style={{ background: tint(product?.color), color: product?.color ?? 'var(--color-primary)' }}>{initials}</div>
                    <span className="submission-meta">
                        {req.submitter ?? req.submitterEmail ?? 'Anonymous'} · {fmtDate(req.createdAt)} · {req.voteCount} {req.voteCount === 1 ? 'vote' : 'votes'}
                    </span>
                    <label className="ho-toggle" style={{ marginLeft: 'auto' }}
                        title="Visible on the board to SuperAdmins and HeadOffice org-unit members only">
                        <input type="checkbox" checked={headOfficeOnly}
                            onChange={e => { const v = e.target.checked; setHeadOfficeOnly(v); onSetHeadOffice(v) }} />
                        <span className="ho-switch" />
                        HO only
                    </label>
                    <div className="submission-actions">
                        <button className="btn btn-secondary" onClick={onEdit}>Edit</button>
                        <button className="btn btn-success" onClick={() => onPublish({ phaseId: req.phaseId || null, isHeadOfficeOnly: headOfficeOnly })}>Publish</button>
                    </div>
                </div>
            </div>
        </div>
    )
}

// ════════════════════════════════════════════════════════════════════════
//  All requests
// ════════════════════════════════════════════════════════════════════════
function RequestsTab({ requests, products, phases }: { requests: AdminRequest[]; products: Product[]; phases: Phase[] }) {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const [editing, setEditing] = useState<AdminRequest | null>(null)
    const [removing, setRemoving] = useState<AdminRequest | null>(null)
    const [statusFilter, setStatusFilter] = useState('all')
    const [searchParams, setSearchParams] = useSearchParams()

    // Deep-link: ?edit=<id> (e.g. from an email) opens that request's edit view once data loads,
    // regardless of its phase/status.
    const editId = searchParams.get('edit')
    useEffect(() => {
        if (editId && !editing) {
            const found = requests.find(r => r.id === editId)
            if (found) setEditing(found)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editId, requests])

    const closeEdit = () => {
        setEditing(null)
        if (searchParams.has('edit')) {
            const next = new URLSearchParams(searchParams)
            next.delete('edit')
            setSearchParams(next, { replace: true })
        }
    }

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['admin-roadmap-requests'] })
        qc.invalidateQueries({ queryKey: ['admin-roadmap-pending-count'] }) // refresh sidebar badge
    }
    const updateMut = useMutation({
        mutationFn: (p: { id: string; body: any }) => apiPut(`/api/admin/roadmap/requests/${p.id}`, token, p.body),
        onSuccess: invalidate,
    })
    const declineMut = useMutation({
        mutationFn: (p: { id: string; reason: string }) => apiPost(`/api/admin/roadmap/requests/${p.id}/decline`, token, { reason: p.reason }),
        onSuccess: invalidate,
    })

    const productName = (id?: string) => products.find(p => p.id === id)?.name ?? '—'
    const shown = statusFilter === 'all' ? requests : requests.filter(r => r.status === statusFilter)

    return (
        <>
            <div className="roadmap-toolbar">
                <select className="form-select" style={{ maxWidth: 200 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                    <option value="all">All statuses</option>
                    <option value="Pending">Pending</option>
                    <option value="PendingConfirmation">Pending email confirmation</option>
                    <option value="Published">Published</option>
                    <option value="Declined">Declined</option>
                    <option value="Archived">Archived</option>
                </select>
            </div>

            <div className="chart-panel">
                <table className="top-sellers-table" style={{ tableLayout: 'auto' }}>
                    <thead>
                        <tr>
                            <th>Title</th>
                            <th>Product</th>
                            <th style={{ width: 160 }}>Phase</th>
                            <th style={{ width: 110 }}>Status</th>
                            <th style={{ width: 70 }}>Votes</th>
                            <th style={{ width: 140 }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {shown.map(r => (
                            <tr key={r.id}>
                                <td>
                                    <div style={{ fontWeight: 600 }}>
                                        {r.isHeadOfficeOnly && <span className="ho-badge" title="HeadOffice / SuperAdmin only">HO</span>}
                                        {r.title}
                                        {r.images && r.images.length > 0 && <span title="Has images" style={{ marginLeft: 6 }}>🖼️</span>}
                                    </div>
                                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>by {r.submitter ?? 'Unknown'} · {fmtDate(r.createdAt)}</div>
                                </td>
                                <td style={{ fontSize: 'var(--text-sm)' }}>{productName(r.productId)}</td>
                                <td>
                                    <select
                                        className="form-select"
                                        value={r.phaseId ?? ''}
                                        onChange={e => updateMut.mutate({ id: r.id, body: { phaseId: e.target.value || null, status: r.status === 'Pending' ? 'Published' : undefined } })}
                                    >
                                        <option value="">— None —</option>
                                        {phases.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                    </select>
                                </td>
                                <td><span className={`status-chip status-${r.status}`}>{r.status}</span></td>
                                <td style={{ fontWeight: 700 }}>{r.voteScore}<span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400, fontSize: 'var(--text-xs)' }}> /{r.voteCount}</span></td>
                                <td style={{ textAlign: 'right' }}>
                                    <button className="btn btn-secondary" style={{ padding: '2px 10px' }} onClick={() => setEditing(r)}>Edit</button>
                                    <button className="btn btn-danger" style={{ padding: '2px 10px', marginLeft: 6 }} onClick={() => setRemoving(r)} title="Remove request…">✕</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {!shown.length && <div className="empty-state"><p>No requests.</p></div>}
            </div>

            {editing && (
                <EditRequestModal
                    req={editing}
                    products={products}
                    phases={phases}
                    onClose={closeEdit}
                    onSave={(body) => { updateMut.mutate({ id: editing.id, body }); closeEdit() }}
                />
            )}

            {removing && (
                <DeleteRequestModal
                    req={removing}
                    onArchive={() => { updateMut.mutate({ id: removing.id, body: { status: 'Archived' } }); setRemoving(null) }}
                    onReject={(reason) => { declineMut.mutate({ id: removing.id, reason }); setRemoving(null) }}
                    onClose={() => setRemoving(null)}
                />
            )}
        </>
    )
}

// Private admin↔requester clarification thread, shown inside the edit modal.
type ClarifyMsg = { id: string; body: string; author: string | null; fromAdmin: boolean; createdAt: string }
function ClarificationsSection({ requestId }: { requestId: string }) {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const [msg, setMsg] = useState('')

    const { data: thread } = useQuery<ClarifyMsg[]>({
        queryKey: ['clarifications', requestId],
        queryFn: () => api(`/api/admin/roadmap/requests/${requestId}/clarifications`, token),
    })
    const askMut = useMutation({
        mutationFn: (message: string) => apiPost(`/api/admin/roadmap/requests/${requestId}/clarifications`, token, { message }),
        onSuccess: () => { setMsg(''); qc.invalidateQueries({ queryKey: ['clarifications', requestId] }) },
    })

    return (
        <div className="form-field" style={{ marginTop: 'var(--space-4)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--space-3)' }}>
            <label className="form-label">💬 Clarifications <span style={{ fontWeight: 400, color: 'var(--color-text-tertiary)' }}>— private; emailed to the requester with a reply link</span></label>
            <div className="clarify-thread">
                {(thread ?? []).length === 0 && (
                    <div className="clarify-empty">No questions yet. Ask the requester to clarify anything you need before triaging.</div>
                )}
                {(thread ?? []).map(c => (
                    <div key={c.id} className={`clarify-msg${c.fromAdmin ? ' admin' : ' requester'}`}>
                        <div className="clarify-meta">{c.author ?? (c.fromAdmin ? 'Admin' : 'Requester')} · {fmtDate(c.createdAt)}</div>
                        <div className="clarify-body">{c.body}</div>
                    </div>
                ))}
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)', alignItems: 'flex-end' }}>
                <textarea className="form-textarea" rows={2} style={{ flex: 1 }}
                    placeholder="Ask the requester a clarifying question…" value={msg} onChange={e => setMsg(e.target.value)} />
                <button className="btn btn-primary" disabled={!msg.trim() || askMut.isPending}
                    onClick={() => askMut.mutate(msg.trim())}>{askMut.isPending ? 'Sending…' : 'Ask requester'}</button>
            </div>
        </div>
    )
}

function EditRequestModal({ req, products, phases, onClose, onSave }: {
    req: AdminRequest; products: Product[]; phases: Phase[]; onClose: () => void; onSave: (body: any) => void
}) {
    const [f, setF] = useState({
        title: req.title, description: req.description, problem: req.problem ?? '', plannedSolution: req.plannedSolution ?? '',
        productId: req.productId ?? '', categoryId: req.categoryId ?? '', phaseId: req.phaseId ?? '', status: req.status,
        targetRelease: req.targetRelease ?? '', imageUrl: req.imageUrl ?? '',
        externalProvider: req.externalProvider ?? '', externalUrl: req.externalUrl ?? '',
    })
    const [headOfficeOnly, setHeadOfficeOnly] = useState(req.isHeadOfficeOnly ?? false)
    const set = (k: keyof typeof f, v: string) => setF(prev => ({ ...prev, [k]: v }))
    const categories = products.find(p => p.id === f.productId)?.categories ?? []

    // ── Owner reassignment ──
    const token = useAuthStore(s => s.accessToken)
    const [owner, setOwner] = useState<{ userId: string | null; name: string; email: string }>({
        userId: req.submittedByUserId ?? null, name: req.submitter ?? '', email: req.submitterEmail ?? '',
    })
    const [ownerDirty, setOwnerDirty] = useState(false)
    const [ownerErr, setOwnerErr] = useState<string | null>(null)
    const [ownerSearch, setOwnerSearch] = useState('')
    const [debouncedOwner, setDebouncedOwner] = useState('')
    useEffect(() => { const t = setTimeout(() => setDebouncedOwner(ownerSearch.trim()), 250); return () => clearTimeout(t) }, [ownerSearch])
    const { data: ownerResults } = useQuery<OwnerUser[]>({
        queryKey: ['roadmap-owner-search', debouncedOwner],
        enabled: debouncedOwner.length >= 2,
        queryFn: () => api(`/api/admin/roadmap/users?q=${encodeURIComponent(debouncedOwner)}`, token),
    })

    const handleSave = () => {
        // A manual owner edit (no linked account) needs a valid email to be reachable.
        if (ownerDirty && !owner.userId) {
            if (!owner.email.trim()) { setOwnerErr('Pick a user or enter an email for the owner.'); return }
            if (!owner.email.includes('@')) { setOwnerErr('Enter a valid owner email.'); return }
        }
        onSave({
            ...f,
            // Empty selects can't bind to Guid? server-side; send the empty GUID,
            // which the API treats as "clear this field".
            productId: f.productId || EMPTY_GUID,
            categoryId: f.categoryId || EMPTY_GUID,
            phaseId: f.phaseId || EMPTY_GUID,
            isHeadOfficeOnly: headOfficeOnly,
            owner: ownerDirty
                ? { userId: owner.userId, name: owner.name.trim() || null, email: owner.email.trim() || null }
                : undefined,
        })
    }

    return (
        <div className="modal-backdrop">
            <div className="modal roadmap-detail">
                <div className="modal-header"><h2>Edit request</h2><button className="modal-close" onClick={onClose}>✕</button></div>
                <div className="modal-body">
                    <div className="form-field owner-field">
                        <label className="form-label">Owner / submitter</label>
                        <div className="owner-current">
                            {owner.name || owner.email
                                ? <>Owner: <strong>{owner.name || owner.email}</strong>{owner.name && owner.email ? ` · ${owner.email}` : ''}
                                    {owner.userId && <span className="owner-tag">account</span>}</>
                                : <span style={{ color: 'var(--color-text-tertiary)' }}>No owner set</span>}
                            <span style={{ color: 'var(--color-text-tertiary)' }}> · created {fmtDate(req.createdAt)}</span>
                        </div>
                        <div className="owner-search">
                            <input className="form-input" placeholder="Search users by name or email…"
                                value={ownerSearch} onChange={e => setOwnerSearch(e.target.value)} />
                            {debouncedOwner.length >= 2 && ownerResults && (
                                <div className="owner-results">
                                    {ownerResults.length === 0 && <div className="owner-empty">No matching users.</div>}
                                    {ownerResults.map(u => (
                                        <button type="button" key={u.id} className="owner-result"
                                            onClick={() => { setOwner({ userId: u.id, name: u.displayName, email: u.email }); setOwnerDirty(true); setOwnerErr(null); setOwnerSearch('') }}>
                                            <strong>{u.displayName}</strong><span>{u.email}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="owner-manual">
                            <span className="owner-or">or set manually</span>
                            <input className="form-input" placeholder="Name" value={owner.name}
                                onChange={e => { const v = e.target.value; setOwner(o => ({ ...o, name: v, userId: null })); setOwnerDirty(true); setOwnerErr(null) }} />
                            <input className="form-input" placeholder="email@company.com" value={owner.email}
                                onChange={e => { const v = e.target.value; setOwner(o => ({ ...o, email: v, userId: null })); setOwnerDirty(true); setOwnerErr(null) }} />
                        </div>
                        {ownerErr && <div className="owner-err">{ownerErr}</div>}
                        {ownerDirty && <div className="owner-note">Saving will email the new owner and subscribe them to this idea's updates.</div>}
                    </div>
                    <div className="form-field"><label className="form-label">Title</label><input className="form-input" value={f.title} onChange={e => set('title', e.target.value)} /></div>
                    <div className="form-field"><label className="form-label">Description</label><textarea className="form-textarea" rows={3} value={f.description} onChange={e => set('description', e.target.value)} /></div>
                    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                        <div className="form-field" style={{ flex: 1 }}><label className="form-label">Problem</label><textarea className="form-textarea" rows={3} value={f.problem} onChange={e => set('problem', e.target.value)} /></div>
                        <div className="form-field" style={{ flex: 1 }}><label className="form-label">Planned solution</label><textarea className="form-textarea" rows={3} value={f.plannedSolution} onChange={e => set('plannedSolution', e.target.value)} /></div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-3)' }}>
                        <div className="form-field"><label className="form-label">Product</label>
                            <select className="form-select" value={f.productId} onChange={e => { set('productId', e.target.value); set('categoryId', '') }}>
                                <option value="">— None —</option>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div className="form-field"><label className="form-label">Category</label>
                            <select className="form-select" value={f.categoryId} onChange={e => set('categoryId', e.target.value)} disabled={!categories.length}>
                                <option value="">— None —</option>
                                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div className="form-field"><label className="form-label">Phase</label>
                            <select className="form-select" value={f.phaseId} onChange={e => set('phaseId', e.target.value)}>
                                <option value="">— None —</option>
                                {phases.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div className="form-field"><label className="form-label">Status</label>
                            <select className="form-select" value={f.status} onChange={e => set('status', e.target.value)}>
                                {['Pending', 'PendingConfirmation', 'Published', 'Declined', 'Archived'].map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div className="form-field"><label className="form-label">Target release</label><input className="form-input" value={f.targetRelease} onChange={e => set('targetRelease', e.target.value)} placeholder="e.g. Q3 2026" /></div>
                        <div className="form-field"><label className="form-label">Image URL</label><input className="form-input" value={f.imageUrl} onChange={e => set('imageUrl', e.target.value)} /></div>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                        <div className="form-field" style={{ flex: 1 }}><label className="form-label">External provider</label><input className="form-input" value={f.externalProvider} onChange={e => set('externalProvider', e.target.value)} placeholder="e.g. monday" /></div>
                        <div className="form-field" style={{ flex: 2 }}><label className="form-label">External URL</label><input className="form-input" value={f.externalUrl} onChange={e => set('externalUrl', e.target.value)} /></div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 'var(--space-2)' }}>
                        <input type="checkbox" checked={headOfficeOnly} onChange={e => setHeadOfficeOnly(e.target.checked)} />
                        <span><strong>HeadOffice only</strong> — visible on the board to SuperAdmins and HeadOffice org-unit members only.</span>
                    </label>
                    {req.images && req.images.length > 0 && (
                        <div className="form-field">
                            <label className="form-label">Attached images</label>
                            <RoadmapGallery images={req.images} />
                        </div>
                    )}
                    <ClarificationsSection requestId={req.id} />
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSave}>Save changes</button>
                </div>
            </div>
        </div>
    )
}

// Removing a request is a deliberate choice, not a raw delete: Archive (soft-delete, no email)
// or Reject (decline with a reason that's emailed to the requester). Dismissible only by an
// action button — never by clicking the backdrop.
function DeleteRequestModal({ req, onArchive, onReject, onClose }: {
    req: AdminRequest; onArchive: () => void; onReject: (reason: string) => void; onClose: () => void
}) {
    const [mode, setMode] = useState<'choose' | 'reject'>('choose')
    const [reason, setReason] = useState('')
    return (
        <div className="modal-backdrop">
            <div className="modal" style={{ maxWidth: 480 }}>
                <div className="modal-header">
                    <h2>Remove request</h2>
                    <button className="modal-close" onClick={onClose}>✕</button>
                </div>
                <div className="modal-body">
                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-3)' }}>{req.title}</div>
                    {mode === 'choose' ? (
                        <div className="delete-choice">
                            <button className="delete-choice-btn" onClick={onArchive}>
                                <span className="delete-choice-icon">🗄️</span>
                                <span className="delete-choice-text">
                                    <strong>Archive</strong>
                                    <span className="delete-choice-desc">Soft-delete: hide it from the board but keep it for history. No email is sent.</span>
                                </span>
                            </button>
                            <button className="delete-choice-btn danger" onClick={() => setMode('reject')}>
                                <span className="delete-choice-icon">🚫</span>
                                <span className="delete-choice-text">
                                    <strong>Reject</strong>
                                    <span className="delete-choice-desc">Decline with a reason. The requester is emailed, and it moves to the Rejected bucket.</span>
                                </span>
                            </button>
                        </div>
                    ) : (
                        <div className="form-field" style={{ margin: 0 }}>
                            <label className="form-label">Reason for rejection <span style={{ color: 'var(--color-text-tertiary)', fontWeight: 400 }}>— emailed to the requester</span></label>
                            <textarea className="form-textarea" rows={4} autoFocus value={reason}
                                onChange={e => setReason(e.target.value)} placeholder="Explain why this idea won't be pursued…" />
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    {mode === 'choose' ? (
                        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                    ) : (
                        <>
                            <button className="btn btn-secondary" onClick={() => setMode('choose')}>← Back</button>
                            <button className="btn btn-danger" disabled={!reason.trim()} onClick={() => onReject(reason.trim())}>Reject &amp; notify</button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

// ════════════════════════════════════════════════════════════════════════
//  Phases
// ════════════════════════════════════════════════════════════════════════
function PhasesTab({ phases }: { phases: Phase[] }) {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-roadmap-phases'] })
    const [adding, setAdding] = useState(false)
    const [confirmPhase, setConfirmPhase] = useState<Phase | null>(null)

    const createMut = useMutation({ mutationFn: (b: any) => apiPost('/api/admin/roadmap/phases', token, b), onSuccess: () => { invalidate(); setAdding(false) } })
    const updateMut = useMutation({ mutationFn: (p: { id: string; b: any }) => apiPut(`/api/admin/roadmap/phases/${p.id}`, token, p.b), onSuccess: invalidate })
    const deleteMut = useMutation({ mutationFn: (id: string) => apiDelete(`/api/admin/roadmap/phases/${id}`, token), onSuccess: invalidate })

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
                <button className="btn btn-primary" onClick={() => setAdding(true)}>＋ Add phase</button>
            </div>
            <div className="chart-panel">
                <table className="top-sellers-table" style={{ tableLayout: 'auto' }}>
                    <thead><tr><th style={{ width: 60 }}>Order</th><th>Phase</th><th style={{ width: 90 }}>Color</th><th style={{ width: 80 }}>Default</th><th style={{ width: 80 }}>Released</th><th style={{ width: 70 }}>Active</th><th style={{ width: 80 }}>Items</th><th style={{ width: 120 }}></th></tr></thead>
                    <tbody>
                        {phases.map(p => <PhaseRow key={p.id} phase={p} onUpdate={(b) => updateMut.mutate({ id: p.id, b })} onDelete={() => setConfirmPhase(p)} />)}
                        {adding && <PhaseEditRow onSave={(b) => createMut.mutate(b)} onCancel={() => setAdding(false)} />}
                    </tbody>
                </table>
            </div>

            {confirmPhase && (
                <ConfirmDialog
                    title="Delete phase"
                    message={<>Delete phase <strong>“{confirmPhase.name}”</strong>? Requests in it become unassigned (they aren't deleted).</>}
                    confirmLabel="Delete phase"
                    danger
                    onConfirm={() => { deleteMut.mutate(confirmPhase.id); setConfirmPhase(null) }}
                    onCancel={() => setConfirmPhase(null)}
                />
            )}
        </>
    )
}

function PhaseRow({ phase, onUpdate, onDelete }: { phase: Phase; onUpdate: (b: any) => void; onDelete: () => void }) {
    const [editing, setEditing] = useState(false)
    if (editing) return <PhaseEditRow phase={phase} onSave={(b) => { onUpdate(b); setEditing(false) }} onCancel={() => setEditing(false)} />
    return (
        <tr style={{ opacity: phase.isActive ? 1 : 0.5 }}>
            <td>{phase.sortOrder}</td>
            <td><span>{phase.icon} <strong>{phase.name}</strong></span>{phase.description && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{phase.description}</div>}</td>
            <td><span className="color-swatch" style={{ background: phase.color ?? 'transparent' }} /></td>
            <td>{phase.isDefault ? '✓' : ''}</td>
            <td>{phase.isTerminal ? '✓' : ''}</td>
            <td>{phase.isActive ? '✓' : ''}</td>
            <td>{phase.requestCount}</td>
            <td style={{ textAlign: 'right' }}>
                <button className="btn btn-secondary" style={{ padding: '2px 10px' }} onClick={() => setEditing(true)}>Edit</button>
                <button className="btn btn-danger" style={{ padding: '2px 10px', marginLeft: 6 }} onClick={onDelete} title="Delete phase…">✕</button>
            </td>
        </tr>
    )
}

function PhaseEditRow({ phase, onSave, onCancel }: { phase?: Phase; onSave: (b: any) => void; onCancel: () => void }) {
    const [f, setF] = useState({
        name: phase?.name ?? '', description: phase?.description ?? '', color: phase?.color ?? '#6366F1', icon: phase?.icon ?? '',
        sortOrder: phase?.sortOrder ?? 0, isDefault: phase?.isDefault ?? false, isTerminal: phase?.isTerminal ?? false, isActive: phase?.isActive ?? true,
    })
    return (
        <tr>
            <td><input className="form-input" type="number" style={{ width: 56 }} value={f.sortOrder} onChange={e => setF({ ...f, sortOrder: +e.target.value })} /></td>
            <td>
                <input className="form-input" placeholder="Name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
                <input className="form-input" placeholder="Description" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} style={{ marginTop: 4 }} />
                <input className="form-input" placeholder="Icon (emoji)" value={f.icon} onChange={e => setF({ ...f, icon: e.target.value })} style={{ marginTop: 4, width: 100 }} />
            </td>
            <td><input type="color" value={f.color} onChange={e => setF({ ...f, color: e.target.value })} style={{ width: 36, height: 28, border: 'none', background: 'none' }} /></td>
            <td><input type="checkbox" checked={f.isDefault} onChange={e => setF({ ...f, isDefault: e.target.checked })} /></td>
            <td><input type="checkbox" checked={f.isTerminal} onChange={e => setF({ ...f, isTerminal: e.target.checked })} /></td>
            <td><input type="checkbox" checked={f.isActive} onChange={e => setF({ ...f, isActive: e.target.checked })} /></td>
            <td></td>
            <td style={{ textAlign: 'right' }}>
                <button className="btn btn-primary" style={{ padding: '2px 10px' }} disabled={!f.name.trim()} onClick={() => onSave(f)}>Save</button>
                <button className="btn btn-secondary" style={{ padding: '2px 10px', marginLeft: 6 }} onClick={onCancel}>Cancel</button>
            </td>
        </tr>
    )
}

// ════════════════════════════════════════════════════════════════════════
//  Products & categories
// ════════════════════════════════════════════════════════════════════════
function ProductsTab({ products }: { products: Product[] }) {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()
    const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-roadmap-products'] })
    const [adding, setAdding] = useState(false)

    const createMut = useMutation({ mutationFn: (b: any) => apiPost('/api/admin/roadmap/products', token, b), onSuccess: () => { invalidate(); setAdding(false) } })
    const updateMut = useMutation({ mutationFn: (p: { id: string; b: any }) => apiPut(`/api/admin/roadmap/products/${p.id}`, token, p.b), onSuccess: invalidate })
    const deleteMut = useMutation({ mutationFn: (id: string) => apiDelete(`/api/admin/roadmap/products/${id}`, token), onSuccess: invalidate })

    const catCreate = useMutation({ mutationFn: (b: any) => apiPost('/api/admin/roadmap/categories', token, b), onSuccess: invalidate })
    const catUpdate = useMutation({ mutationFn: (p: { id: string; b: any }) => apiPut(`/api/admin/roadmap/categories/${p.id}`, token, p.b), onSuccess: invalidate })
    const catDelete = useMutation({ mutationFn: (id: string) => apiDelete(`/api/admin/roadmap/categories/${id}`, token), onSuccess: invalidate })

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-3)' }}>
                <button className="btn btn-primary" onClick={() => setAdding(true)}>＋ Add product</button>
            </div>
            {adding && (
                <div className="chart-panel" style={{ marginBottom: 'var(--space-3)' }}>
                    <ProductEditor onSave={(b) => createMut.mutate(b)} onCancel={() => setAdding(false)} />
                </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {products.map(p => (
                    <ProductCard
                        key={p.id}
                        product={p}
                        onUpdate={(b) => updateMut.mutate({ id: p.id, b })}
                        onDelete={() => deleteMut.mutate(p.id)}
                        onAddCategory={(name) => catCreate.mutate({ productId: p.id, name })}
                        onUpdateCategory={(id, b) => catUpdate.mutate({ id, b })}
                        onDeleteCategory={(id) => catDelete.mutate(id)}
                    />
                ))}
            </div>
        </>
    )
}

function ProductCard({ product, onUpdate, onDelete, onAddCategory, onUpdateCategory, onDeleteCategory }: {
    product: Product; onUpdate: (b: any) => void; onDelete: () => void
    onAddCategory: (name: string) => void; onUpdateCategory: (id: string, b: any) => void; onDeleteCategory: (id: string) => void
}) {
    const [editing, setEditing] = useState(false)
    const [newCat, setNewCat] = useState('')
    const [confirmDel, setConfirmDel] = useState(false)
    const [catRename, setCatRename] = useState<Category | null>(null)
    const [catDel, setCatDel] = useState<Category | null>(null)

    return (
        <div className="chart-panel" style={{ padding: 'var(--space-4)', opacity: product.isActive ? 1 : 0.6 }}>
            {editing ? (
                <ProductEditor product={product} onSave={(b) => { onUpdate(b); setEditing(false) }} onCancel={() => setEditing(false)} />
            ) : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <span className="color-swatch" style={{ background: product.color ?? 'transparent' }} />
                        <span style={{ fontSize: 'var(--text-lg)' }}>{product.icon}</span>
                        <div>
                            <div style={{ fontWeight: 700 }}>{product.name} {!product.isActive && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>(inactive)</span>}</div>
                            {product.description && <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>{product.description}</div>}
                        </div>
                    </div>
                    <div>
                        <button className="btn btn-secondary" style={{ padding: '2px 10px' }} onClick={() => setEditing(true)}>Edit</button>
                        <button className="btn btn-danger" style={{ padding: '2px 10px', marginLeft: 6 }} onClick={() => setConfirmDel(true)} title="Delete product…">✕</button>
                    </div>
                </div>
            )}

            {/* Categories */}
            <div style={{ marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border-subtle)' }}>
                <div className="roadmap-category-label" style={{ margin: '0 0 var(--space-2)' }}>Categories</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                    {product.categories.map(c => (
                        <span key={c.id} className="roadmap-chip" style={{ gap: 6 }}>
                            {c.name}
                            <button onClick={() => setCatRename(c)} title="Rename category…" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✎</button>
                            <button onClick={() => setCatDel(c)} title="Delete category…" style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-error)' }}>✕</button>
                        </span>
                    ))}
                    {!product.categories.length && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>No categories yet.</span>}
                </div>
                <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)', maxWidth: 320 }}>
                    <input className="form-input" placeholder="New category name" value={newCat} onChange={e => setNewCat(e.target.value)} />
                    <button className="btn btn-secondary" disabled={!newCat.trim()} onClick={() => { onAddCategory(newCat.trim()); setNewCat('') }}>Add</button>
                </div>
            </div>

            {confirmDel && (
                <ConfirmDialog
                    title="Delete product"
                    message={<>Delete <strong>“{product.name}”</strong> and its categories? Ideas keep their data but lose this product assignment.</>}
                    confirmLabel="Delete product"
                    danger
                    onConfirm={() => { onDelete(); setConfirmDel(false) }}
                    onCancel={() => setConfirmDel(false)}
                />
            )}
            {catRename && (
                <PromptDialog
                    title="Rename category"
                    label="Category name"
                    defaultValue={catRename.name}
                    confirmLabel="Rename"
                    onConfirm={(name) => { onUpdateCategory(catRename.id, { productId: product.id, name }); setCatRename(null) }}
                    onCancel={() => setCatRename(null)}
                />
            )}
            {catDel && (
                <ConfirmDialog
                    title="Delete category"
                    message={<>Delete category <strong>“{catDel.name}”</strong>? Ideas in it keep their data but lose this category.</>}
                    confirmLabel="Delete category"
                    danger
                    onConfirm={() => { onDeleteCategory(catDel.id); setCatDel(null) }}
                    onCancel={() => setCatDel(null)}
                />
            )}
        </div>
    )
}

function ProductEditor({ product, onSave, onCancel }: { product?: Product; onSave: (b: any) => void; onCancel: () => void }) {
    const [f, setF] = useState({
        name: product?.name ?? '', description: product?.description ?? '', color: product?.color ?? '#6366F1',
        icon: product?.icon ?? '', sortOrder: product?.sortOrder ?? 0, isActive: product?.isActive ?? true,
    })
    return (
        <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 'var(--space-3)', alignItems: 'end' }}>
                <div className="form-field" style={{ margin: 0 }}><label className="form-label">Name</label><input className="form-input" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
                <div className="form-field" style={{ margin: 0 }}><label className="form-label">Icon (emoji)</label><input className="form-input" value={f.icon} onChange={e => setF({ ...f, icon: e.target.value })} /></div>
                <div className="form-field" style={{ margin: 0 }}><label className="form-label">Color</label><input type="color" value={f.color} onChange={e => setF({ ...f, color: e.target.value })} style={{ width: 48, height: 32, border: 'none', background: 'none' }} /></div>
                <div className="form-field" style={{ margin: 0 }}><label className="form-label">Order</label><input className="form-input" type="number" value={f.sortOrder} onChange={e => setF({ ...f, sortOrder: +e.target.value })} /></div>
                <div className="form-field" style={{ margin: 0 }}><label className="form-label">Active</label><input type="checkbox" checked={f.isActive} onChange={e => setF({ ...f, isActive: e.target.checked })} /></div>
            </div>
            <div className="form-field" style={{ marginTop: 'var(--space-3)' }}><label className="form-label">Description</label><input className="form-input" value={f.description} onChange={e => setF({ ...f, description: e.target.value })} /></div>
            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                <button className="btn btn-primary" disabled={!f.name.trim()} onClick={() => onSave(f)}>Save</button>
            </div>
        </div>
    )
}
